import React, { useState, useEffect } from "react";
import { Row, Col, Button, Form } from "react-bootstrap";
import Modal from "react-modal";
import classNames from "classnames/bind";
import style from "./index.module.css";
import { toast } from "react-toastify";
import { set } from "lodash";



const cx = classNames.bind(style);

// 定義介面 WarrantVerifyModelProps，包括組件的屬性
interface WarrantVerifyModelProps {
  isOpen: boolean;
  handleClose: () => void;
  cu_id: string;
  showQueryResult: boolean;
  setShowQueryResult: (value: boolean) => void;
}


// 表單狀態
interface FormState {
  idNumber: string;
  warrantCode: string;
  transactionDate: string;
  purchaseAmount: string;
  agree: boolean;
  agreePersonalData: boolean;
}
interface ErrorState {
  idNumber?: string;
  warrantCode?: string;
  purchaseAmount?: string;
  agree?: string;
  agreePersonalData?: string;
}

// 定義函數組件 WarrantVerifyModel
const WarrantVerifyModel: React.FC<WarrantVerifyModelProps> = ({
  isOpen,
  handleClose,
  cu_id,
  showQueryResult,
  setShowQueryResult,
}) => {
  const [localCuId, setLocalCuId] = useState<string>(cu_id); //當下的cuid
  const [formState, setFormState] = useState<FormState>({
    idNumber: "",
    warrantCode: "",
    transactionDate: "",
    purchaseAmount: "",
    agree: false,
    agreePersonalData: false,
  });
  const [queryData, setQueryData] = useState<any[]>([]); // 查詢到的交易資料
  const [errors, setErrors] = useState<ErrorState>({}); // 表單錯誤信息
  const [showFirstVerifyModal, setShowFirstVerifyModal] = useState(false); // 首次驗證模態窗
  const [showSuccessModal, setShowSuccessModal] = useState(false); // 提交成功模態窗
  const [fetchedId, setFetchedId] = useState<string | null>(null); // 查詢到的身分證號碼
  const [realId, setRealId] = useState<string | null>(null); // 真實身分證號碼
  const [queryDisabled, setQueryDisabled] = useState(true); // 身分證查詢是否禁用

  // 監聽 cu_id 變化，更新 localCuId
  useEffect(() => {
    setLocalCuId(cu_id);
  }, [cu_id]);
  // 當模態窗打開時，抓取登錄紀錄資料
  useEffect(() => {
    if (isOpen) {
      fetchQueryData();
    }
  }, [isOpen]);

  // 當 fetchedId 變化時，更新表單狀態及查詢禁用狀態
  useEffect(() => {
    if (fetchedId) {
      setFormState((prevState) => ({
        ...prevState,
        idNumber: fetchedId,
      }));
      setQueryDisabled(false);
    } else {
      setQueryDisabled(true);
    }
  }, [fetchedId]);
  // 抓取登錄紀錄資料
  const fetchQueryData = async () => {
    try {
      const response = await fetch('/api2/Warrant/GetTradeLogin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ cu_id: localCuId })
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Request failed with status ${response.status}: ${errorText}`);
      }
      const result = await response.json();
      if (result.err === '0000') {
        const formattedTradeList = result.tradelist.map((item: any) => ({
          ...item,
          trade_date: item.trade_date.split('T')[0]
        }));
        setQueryData(formattedTradeList);
        if (result.id) {
          setRealId(result.full_id);
          setFetchedId(result.id);
          setFormState((prevState) => ({
            ...prevState,
            idNumber: result.id
          }));
          setErrors((prevErrors) => ({
            ...prevErrors,
            idNumber: undefined
          }));
        } else {
          setFetchedId("");
          setRealId("");
          setFormState((prevState) => ({
            ...prevState,
            idNumber: ""
          }));
        }
      } else {
        console.error('Error fetching query data:', result);
      }
    } catch (error) {
      console.error('Error fetching query data:', error);
    }
  };

  // 處理表單輸入變更
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: type === "checkbox" ? checked : value,
    }));
  };
  // 驗證表單輸入
  const validateForm = () => {
    const newErrors: ErrorState = {};
    const idNumberRegex = /^[A-Z][1-2][0-9]{8}$/;
    if (realId && !idNumberRegex.test(realId)) {
      newErrors.idNumber = "身分證字號格式錯誤";
    }
    const warrantCodeRegex = /^(\d{6}|\d{5}[A-Za-z0-9])$/;
    if (!warrantCodeRegex.test(formState.warrantCode)) {
      newErrors.warrantCode = "權證代碼格式錯誤";
    }
    if (!formState.purchaseAmount) {
      newErrors.purchaseAmount = "請輸入金額";
    }
    if (!formState.agree) {
      newErrors.agree = "尚未勾選";
    }
    if (!formState.agreePersonalData) {
      newErrors.agreePersonalData = "尚未勾選";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 提交表單處理
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      if (!fetchedId) {
        // 如果第一次驗證身分證，顯示首次驗證模態窗
        setShowFirstVerifyModal(true);
      } else {
        performSubmit(); // 執行提交操作
      }
    }
  };

  // 真正提交資料到後端的函數
  const performSubmit = async () => {
    const requestData = {
      cu_id: localCuId,
      stock_id: formState.warrantCode,
      trade_date: new Date(formState.transactionDate).toISOString(),
      amt: parseFloat(formState.purchaseAmount),
      pid: formState.idNumber,
    };

    try {
      const response = await fetch('/api2/Warrant/AddTradeLogin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`${response.status}: ${errorText}`);
      }
      const result = await response.json();
      if (result.err === '0000') {
        setShowSuccessModal(true);
        setQueryDisabled(false);
        setFormState((prevState) => ({
          ...prevState,
          warrantCode: "",
          transactionDate: "",
          purchaseAmount: "",
          agree: false,
          agreePersonalData: false,
        }));
      } else {
        console.error('Error submitting form:', result);
        toast.error(result.message);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };
  // 刪除查詢結果中的某一筆資料
  const handleDelete = async (index: number) => {
    const deleteItem = queryData[index];
    try {
      const response = await fetch('/api2/Warrant/DelTradeLogin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          cu_id: localCuId,
          stock_id: deleteItem.stock_id,
          trade_date: new Date(deleteItem.trade_date).toISOString()
        })
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Request failed with status ${response.status}: ${errorText}`);
      }
      const result = await response.json();
      if (result.err === '0000') {
        setQueryData((prevData) => prevData.filter((_, i) => i !== index));
        toast.success('刪除資料成功');
      } else {
        console.error('Error deleting item:', result);
        toast.error(result.message || '刪除資料時出錯');
      }
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error('刪除資料時出錯');
    }
  };

  // 首次驗證模態窗關閉處理
  const handleFirstVerifyClose = () => {
    setShowFirstVerifyModal(false);
    performSubmit(); // 執行提交操作
  };

  // 成功模態窗關閉處理
  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    setFormState({
      idNumber: "",
      warrantCode: "",
      transactionDate: "",
      purchaseAmount: "",
      agree: false,
      agreePersonalData: false,
    });
    handleClose();
  };

  // 查詢下一筆資料
  const handleNextEntry = () => {
    setShowSuccessModal(false);
  };

  // 查詢資料處理
  const handleQuery = () => {
    setShowQueryResult(true);
    fetchQueryData();
  };

  // 關閉查詢結果
  const handleQueryClose = () => {
    setShowQueryResult(false);
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        contentLabel=""
        onRequestClose={handleClose}
        className={cx("Modal")}
        overlayClassName="Overlay"
        shouldCloseOnOverlayClick={false}
        ariaHideApp={false}
      >
        {!showQueryResult ? (
          <div>
            <div className={cx("modalTitleContainer")}>
              <div className={cx("modalTitle")}>權證資料登錄</div>
              <div className={cx("modalRight")}>
                <button className={cx("modalCloseButton")} onClick={handleClose}>
                  <span className={cx("modalCloseButtonFont")}>&times;</span>
                </button>
              </div>
            </div>
            <div className={cx("modalContent")}>
              <p className={cx("description")}>
                請填寫本階段交易成功的權證為抽獎資訊
              </p>
              <p className={cx("description")} style={{ textAlign: "start", color: "#fff" }}>
                本抽獎活動參加者僅限使用本人身分證字號，該身分證字號於每個活動階段登錄上限50筆。若交易日期相同，同一檔權證交易筆數多於1次，則此權證在登錄「已交易的權證」時，將認列為一筆，不得重複登錄。
              </p>
              <Form onSubmit={handleSubmit}>
                <Form.Group as={Row} className="mb-3">
                  <Form.Label column sm="4" className={cx("formLabel")}>
                    身分證字號
                  </Form.Label>
                  <Col sm="8">
                    <Form.Control
                      type="text"
                      name="idNumber"
                      value={formState.idNumber}
                      onChange={handleInputChange}
                      maxLength={10}
                      disabled={!queryDisabled}
                    />
                    {errors.idNumber && <div className={cx("error")}>{errors.idNumber}</div>}
                  </Col>
                </Form.Group>

                <Form.Group as={Row} className="mb-3">
                  <Form.Label column sm="4" className={cx("formLabel")}>
                    權證代碼
                  </Form.Label>
                  <Col sm="8">
                    <Form.Control
                      type="text"
                      name="warrantCode"
                      value={formState.warrantCode}
                      onChange={handleInputChange}
                      maxLength={6}
                    />
                    {errors.warrantCode && <div className={cx("error")}>{errors.warrantCode}</div>}
                  </Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-3">
                  <Form.Label column sm="4" className={cx("formLabel")}>
                    交易日期
                  </Form.Label>
                  <Col sm="8">
                    <Form.Control
                      type="date"
                      name="transactionDate"
                      value={formState.transactionDate}
                      onChange={handleInputChange}
                      placeholder="請輸入日期yyyy/mm/dd"
                    />
                  </Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-3">
                  <Form.Label column sm="4" className={cx("formLabel")}>
                    買入金額
                  </Form.Label>
                  <Col sm="8">
                    <Form.Control
                      type="number"
                      name="purchaseAmount"
                      value={formState.purchaseAmount}
                      onChange={handleInputChange}
                      inputMode="numeric"
                      maxLength={6}
                      onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                        if (e.target.value.length > 6) {
                          e.target.value = e.target.value.slice(0, 6);
                        }
                      }}
                    />

                    {/* 錯誤訊息，與輸入框對齊 */}
                    {errors.purchaseAmount && (
                      <div className={cx("error")} style={{ marginTop: '5px' }}>
                        {errors.purchaseAmount}
                      </div>
                    )}

                    {/* 新的提示文字，與錯誤訊息下對齊 */}
                    <div style={{ fontSize: "15px", color: "#fff", marginTop: '5px' }}>
                      *買入總金額=權證價格 X 買入股數
                    </div>
                  </Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-3">
                  <Form.Label column sm="4"></Form.Label>
                  <Col sm="12" className={cx("formCheck")}>
                    <Form.Check
                      type="checkbox"
                      name="agree"
                      checked={formState.agree}
                      onChange={handleInputChange}
                      label={
                        <>
                          同意
                          <span
                            onClick={() => {
                              (window.parent as any).showTradEventModalId()
                            }}
                            style={{ textDecoration: "underline", cursor: "pointer", color: "#fff" }}
                          >
                            《活動辦法》
                          </span>
                          內容。
                        </>
                      }
                    />
                    {errors.agree && <div className={cx("error")}>{errors.agree}</div>}
                  </Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-3">
                  <Form.Label column sm="4"></Form.Label>
                  <Col sm="12" className={cx("formCheck")}>
                    <Form.Check
                      type="checkbox"
                      name="agreePersonalData"
                      checked={formState.agreePersonalData}
                      onChange={handleInputChange}
                      label={
                        <>
                          同意
                          <span
                            onClick={() => {
                              (window.parent as any).showTradEventModalId()
                            }}
                            style={{ textDecoration: "underline", cursor: "pointer", color: "#fff" }}
                          >
                            《蒐集個人資料告知事項與同意書》
                          </span>
                          內容。
                        </>
                      }
                    />
                    {errors.agreePersonalData && <div className={cx("error")}>{errors.agreePersonalData}</div>}
                  </Col>
                </Form.Group>
                <div className={cx("button-group")}>
                  <Col style={{ display: "inline-grid" }}>
                    <Button
                      className={cx("order-button-cancel")}
                      onClick={handleQuery}
                      disabled={fetchedId === "" && queryDisabled}
                    >
                      查詢
                    </Button>
                  </Col>
                  <Col style={{ display: "inline-grid" }}>
                    <Button type="submit" className={cx("order-button")}>
                      送出
                    </Button>
                  </Col>
                </div>
              </Form>
            </div>
          </div>
        ) : (
          <div>
            <div className={cx("modalTitleContainer")}>
              <div
                className={cx("modalTitle")}>登錄紀錄
              </div>
              <div className={cx("modalRight")}>
                <button className={cx("modalCloseButton")} onClick={handleClose}>
                  <span className={cx("modalCloseButtonFont")}>&times;</span>
                </button>
              </div>
            </div>

            <div className={cx("modalContent")}>
              <p className={cx("description")} style={{ textAlign: "start", color: "#fff" }}>
                本抽獎活動參加者僅限使用本人身分證字號，該身分證字號於每個活動階段登錄上限為50筆。若交易日期相同，同一檔權證交易筆數多於1次，則此權證在登錄「已交易的權證」時，將認列為一筆，不得重複登錄。
              </p>
              <ul className={cx("recordList")}>
                <li className={cx("recordRow")}>
                  <span>序號</span>
                  <span>權證代碼</span>
                  <span>交易日期</span>
                  <span>買入金額</span>
                  <span>功能</span>
                </li>
                {queryData.map((record, index) => (
                  <li className={cx("recordRow")} key={index}>
                    <span>&nbsp;&nbsp;&nbsp;{index + 1}</span>
                    <span>&nbsp;&nbsp;&nbsp;{record.stock_id}</span>
                    <span>&nbsp;&nbsp;&nbsp;{record.trade_date}</span>
                    <span>&nbsp;&nbsp;&nbsp;&nbsp;{record.amt}</span>
                    <span>
                      <Button variant="danger" onClick={() => handleDelete(index)}>刪除</Button>
                    </span>
                  </li>
                ))}
              </ul>
              <div className={cx("button-group")}>
                <Button className={cx("order-button")} onClick={handleQueryClose}>
                  查詢/登錄下一筆
                </Button>
              </div>
            </div>
          </div>
        )}
      </Modal>
      <Modal
        isOpen={showFirstVerifyModal && !fetchedId}
        contentLabel="身分證確認"
        onRequestClose={() => setShowFirstVerifyModal(false)}
        className={cx("Modal")}
        overlayClassName="Overlay"
        shouldCloseOnOverlayClick={false}
        ariaHideApp={false}
      >
        <div className={cx("modalTitleContainer")}>
          <div className={cx("modalRight")}>
            <button className={cx("modalCloseButton")} onClick={() => setShowFirstVerifyModal(false)}>
              <span className={cx("modalCloseButtonFont")}>&times;</span>
            </button>
          </div>
        </div>

        <div className={cx("modalContent")}>
          <div className={cx("description")} style={{ fontSize: "18px", color: "#FFF" }}>
            請再次確認身分證字號是否正確
          </div>
          <p className={cx("description")} style={{ fontSize: "18px", color: "#FFF" }}>
            身分證字號：{formState.idNumber}
          </p>
          <p className={cx("error")} style={{ textAlign: "center" }}>* 身分證字號一經送出無法修改</p>
          <div className={cx("button-group")}>
            <Button className={cx("order-button")} onClick={handleFirstVerifyClose}>
              送出
            </Button>
            <Button className={cx("order-button-cancel")} onClick={() => setShowFirstVerifyModal(false)}>
              取消
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={showSuccessModal}
        contentLabel="提交成功"
        onRequestClose={handleSuccessModalClose}
        className={cx("Modal")}
        overlayClassName="Overlay"
        shouldCloseOnOverlayClick={false}
        ariaHideApp={false}
      >
        <div className={cx("modalTitleContainer")}>
          <div className={cx("modalTitle")}>提交成功</div>
          <div className={cx("modalRight")}>
            <button className={cx("modalCloseButton")} onClick={handleSuccessModalClose}>
              <span className={cx("modalCloseButtonFont")}>&times;</span>
            </button>
          </div>
        </div>

        <div className={cx("modalContent")}>
          <p style={{ fontSize: "18px", color: "#49FFFF", textAlign: "center" }}>
            權證資料提交成功！
          </p>
          <div className={cx("button-group")}>
            <Col style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
              <Button className={cx("order-button")} onClick={handleNextEntry}>
                確認
              </Button>
            </Col>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default WarrantVerifyModel;
